import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { MessageSquare, Send, Loader2, Search, CheckCircle2, Lock, Trash2, ChevronLeft, Paperclip, ExternalLink, FileText, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;
const MAX_BYTES = 5 * 1024 * 1024;

const AttachmentBlock = ({ attachment, mine }) => {
  if (!attachment?.url) return null;
  if (attachment.is_image) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block mt-1">
        <img
          src={attachment.url}
          alt={attachment.filename || "image"}
          className="max-h-52 max-w-full rounded-lg border border-[#1a3a6a] object-cover"
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className={`mt-1 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${
        mine ? "bg-[#f4cc1e] text-[#091c3c] border-[#f4cc1e]" : "bg-[#0d2a5a] text-white border-[#1a3a6a]"
      }`}
    >
      <Paperclip size={13} /> {attachment.filename || "Download file"}
    </a>
  );
};

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const AdminChat = () => {
  const { user } = useAuth();
  const [filter, setFilter] = useState("open"); // open | waiting | active | closed
  const [search, setSearch] = useState("");
  const [queue, setQueue] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [closing, setClosing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);

  const lastSeqRef = useRef(0);
  const bottomRef = useRef(null);
  const sendingRef = useRef(false);
  const claimingRef = useRef(false);
  const closingRef = useRef(false);
  const fileInputRef = useRef(null);

  const isClosed = conversation?.status === "closed";
  const ownedByMe = conversation?.assigned_admin_id === user?.id;
  const canReply = conversation?.status === "active" && ownedByMe;

  const fetchQueue = useCallback(async () => {
    try {
      let res;
      if (filter === "closed") {
        const params = new URLSearchParams({ status: "closed" });
        if (search.trim()) params.set("search", search.trim());
        res = await axios.get(`${API}/admin/chat/history?${params.toString()}`);
      } else {
        res = await axios.get(`${API}/admin/chat/queue?status=${filter}`);
      }
      setQueue(res.data || []);
    } catch (e) {
      // transient
    }
  }, [filter, search]);

  // Poll the queue.
  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Load chat templates once for the composer picker.
  useEffect(() => {
    axios.get(`${API}/admin/chat-templates`)
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  }, []);

  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.seq));
      const merged = [...prev];
      for (const m of incoming) if (!seen.has(m.seq)) merged.push(m);
      merged.sort((a, b) => a.seq - b.seq);
      return merged;
    });
    lastSeqRef.current = incoming.reduce((mx, m) => Math.max(mx, m.seq), lastSeqRef.current);
  }, []);

  // Load a conversation when selected.
  const selectConversation = async (conv) => {
    setSelectedId(conv.id);
    setConversation(conv);
    setMessages([]);
    lastSeqRef.current = 0;
    setDraft("");
    try {
      if (conv.status === "closed") {
        const res = await axios.get(`${API}/admin/chat/${conv.id}`);
        setConversation(res.data.conversation);
        mergeMessages(res.data.messages);
      } else {
        const res = await axios.get(`${API}/admin/chat/${conv.id}/messages?after_seq=0`);
        setConversation(res.data.conversation);
        mergeMessages(res.data.messages);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load conversation");
    }
  };

  // Poll the open selected conversation for new messages + status.
  useEffect(() => {
    if (!selectedId || isClosed) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API}/admin/chat/${selectedId}/messages?after_seq=${lastSeqRef.current}`
        );
        setConversation(res.data.conversation);
        mergeMessages(res.data.messages);
      } catch (e) {
        // transient
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [selectedId, isClosed, mergeMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const claim = async () => {
    if (!conversation?.id || claimingRef.current) return;
    claimingRef.current = true;
    setClaiming(true);
    try {
      const res = await axios.post(`${API}/admin/chat/${conversation.id}/claim`);
      setConversation(res.data);
      toast.success("Chat claimed");
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim chat");
      fetchQueue();
    } finally {
      setClaiming(false);
      claimingRef.current = false;
    }
  };

  const openDashboard = async () => {
    if (!conversation?.customer_id) return;
    try {
      const res = await axios.post(`${API}/auth/impersonate`, { user_id: conversation.customer_id });
      const token = res.data?.access_token;
      if (!token) { toast.error("Could not open dashboard"); return; }
      window.open(`${window.location.origin}/view-as#${encodeURIComponent(token)}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open dashboard");
    }
  };

  const renderTemplate = (body, ctx) =>
    (body || "").replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (_, k) => String(ctx[k.toLowerCase()] ?? ""));

  const insertTemplate = (t) => {
    const company = conversation?.customer_name || "";
    const rendered = renderTemplate(t.body, { name: company, company });
    setDraft((d) => (d ? d + "\n" + rendered : rendered));
  };

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body || !conversation?.id || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/chat/${conversation.id}/messages`, { body });
      setDraft("");
      mergeMessages([res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("File is larger than 5MB.");
      return;
    }
    uploadFile(f);
  };

  const uploadFile = async (f) => {
    if (!conversation?.id || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const res = await axios.post(`${API}/admin/chat/${conversation.id}/upload`, form);
      mergeMessages([res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload file");
    } finally {
      setUploading(false);
    }
  };

  const deleteConversation = async (conv) => {
    if (
      !window.confirm(
        `Delete the chat with ${conv.customer_name}? This permanently removes the conversation and all its messages.`
      )
    )
      return;
    try {
      await axios.delete(`${API}/admin/chat/${conv.id}`);
      toast.success("Chat deleted");
      if (selectedId === conv.id) {
        setSelectedId(null);
        setConversation(null);
        setMessages([]);
      }
      setQueue((q) => q.filter((c) => c.id !== conv.id));
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete chat");
    }
  };

  const closeChat = async () => {
    if (!conversation?.id || closingRef.current) return;
    if (!window.confirm("Close this chat and email the full transcript to the customer?")) return;
    closingRef.current = true;
    setClosing(true);
    try {
      const res = await axios.post(`${API}/admin/chat/${conversation.id}/close`);
      toast.success(res.data?.emailed ? "Chat closed — transcript emailed" : "Chat closed (email not sent)");
      setConversation((c) => ({ ...c, status: "closed" }));
      fetchQueue();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close chat");
    } finally {
      setClosing(false);
      closingRef.current = false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const backToQueue = () => {
    setSelectedId(null);
    setConversation(null);
    setMessages([]);
  };

  const statusBadge = (conv) => {
    if (conv.status === "waiting")
      return <Badge className="bg-[#f4cc1e]/20 text-[#f4cc1e] border-[#f4cc1e]/30">Waiting</Badge>;
    if (conv.status === "active")
      return <Badge className="bg-green-600/20 text-green-400 border-green-500/30">Active</Badge>;
    return <Badge className="bg-white/10 text-white/60 border-white/20">Closed</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MessageSquare size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Support Chat</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-200px)] min-h-[460px]">
        {/* Queue */}
        <Card className={`bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg flex-col lg:col-span-1 overflow-hidden lg:flex ${selectedId ? "hidden" : "flex"}`}>
          <div className="p-3 border-b border-[#1a3a6a] space-y-2">
            <Select value={filter} onValueChange={(v) => { setFilter(v); setSelectedId(null); setConversation(null); }}>
              <SelectTrigger className="w-full h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#091c3c] border-[#1a3a6a]">
                <SelectItem value="open">Open (Waiting + Active)</SelectItem>
                <SelectItem value="waiting">Waiting</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">History (Closed)</SelectItem>
              </SelectContent>
            </Select>
            {filter === "closed" && (
              <div className="relative">
                <Search size={14} className="absolute left-2 top-2.5 text-white/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search customer…"
                  className="pl-7 h-9 bg-[#0d2a5a] border-[#1a3a6a] text-white text-sm"
                />
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {queue.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-sm">No conversations</div>
            ) : (
              queue.map((conv) => (
                <div
                  key={conv.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectConversation(conv)}
                  onKeyDown={(e) => { if (e.key === "Enter") selectConversation(conv); }}
                  className={`group w-full text-left px-3 py-2.5 border-b border-[#1a3a6a] hover:bg-[#0d2a5a] transition-colors cursor-pointer ${
                    selectedId === conv.id ? "bg-[#0d2a5a]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-sm font-medium truncate">{conv.customer_name}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {conv.unread > 0 && (
                        <span className="bg-[#f4cc1e] text-[#091c3c] text-[10px] font-bold rounded-full px-1.5 py-0.5">
                          {conv.unread}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteConversation(conv); }}
                        title="Delete chat"
                        className="text-white/30 hover:text-red-400 transition-colors p-0.5"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    {statusBadge(conv)}
                    <span className="text-[10px] text-white/40">{formatTime(conv.last_message_at)}</span>
                  </div>
                  {conv.status === "active" && conv.assigned_admin_name && (
                    <div className="text-[10px] text-white/40 mt-1 flex items-center gap-1">
                      <Lock size={10} /> {conv.assigned_admin_id === user?.id ? "You" : conv.assigned_admin_name}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Conversation pane */}
        <Card className={`bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg flex-col lg:col-span-2 overflow-hidden lg:flex ${selectedId ? "flex" : "hidden"}`}>
          {!conversation ? (
            <div className="flex-1 flex flex-col items-center justify-center text-white/40">
              <MessageSquare size={48} className="mb-4 opacity-50" />
              <p>Select a conversation</p>
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#1a3a6a] flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <button
                    onClick={backToQueue}
                    title="Back to queue"
                    className="lg:hidden shrink-0 text-white/70 hover:text-[#f4cc1e] -ml-1"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{conversation.customer_name}</div>
                    <div className="text-[11px] text-white/40">
                      {conversation.status === "waiting" && "Unclaimed"}
                      {conversation.status === "active" &&
                        `Claimed by ${ownedByMe ? "you" : conversation.assigned_admin_name}`}
                      {isClosed && `Closed${conversation.closed_at ? " · " + formatTime(conversation.closed_at) : ""}`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 shrink-0 flex-wrap">
                  <Button
                    onClick={openDashboard}
                    variant="outline"
                    title="Open this customer's dashboard in a new tab"
                    className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-9"
                  >
                    <ExternalLink size={15} className="mr-1" /> Dashboard
                  </Button>
                  {conversation.status === "waiting" && (
                    <Button
                      onClick={claim}
                      disabled={claiming}
                      className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-9"
                    >
                      {claiming ? <Loader2 size={16} className="animate-spin" /> : "Claim"}
                    </Button>
                  )}
                  {conversation.status === "active" && ownedByMe && (
                    <Button
                      onClick={closeChat}
                      disabled={closing}
                      variant="outline"
                      className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-9"
                    >
                      {closing ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 size={15} className="mr-1" /> Close
                          <span className="hidden sm:inline">&nbsp;& Email</span>
                        </>
                      )}
                    </Button>
                  )}
                  <Button
                    onClick={() => deleteConversation(conversation)}
                    variant="outline"
                    size="icon"
                    title="Delete chat"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 w-9"
                  >
                    <Trash2 size={15} />
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((m) => {
                  if (m.sender_role === "system") {
                    return (
                      <div key={m.seq} className="flex justify-center">
                        <div className="max-w-[92%] bg-[#0d2a5a]/60 border border-[#1a3a6a] text-white/70 text-xs rounded-lg px-3 py-2 whitespace-pre-wrap">
                          <span className="text-[#f4cc1e]/80 font-medium">Auto-reply · </span>
                          {m.body}
                        </div>
                      </div>
                    );
                  }
                  const fromAdmin = m.sender_role === "admin";
                  return (
                    <div key={m.seq} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] flex flex-col ${fromAdmin ? "items-end" : "items-start"}`}>
                        {m.body && (
                          <div
                            className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
                              fromAdmin
                                ? "bg-[#f4cc1e] text-[#091c3c]"
                                : "bg-[#0d2a5a] text-white border border-[#1a3a6a]"
                            }`}
                          >
                            {m.body}
                          </div>
                        )}
                        {m.attachment && <AttachmentBlock attachment={m.attachment} mine={fromAdmin} />}
                        <span className="text-[10px] text-white/40 mt-1 px-1">
                          {m.sender_name} · {formatTime(m.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div className="border-t border-[#1a3a6a] p-3">
                {isClosed ? (
                  <p className="text-center text-sm text-white/40 py-2">
                    This conversation is closed (read-only).
                  </p>
                ) : canReply ? (
                  <div className="flex gap-2 items-end">
                    <input ref={fileInputRef} type="file" hidden onChange={onPickFile} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          title="Insert a template"
                          className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-10 px-2 shrink-0"
                        >
                          <FileText size={16} /> <ChevronDown size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="bg-[#091c3c] border-[#1a3a6a] max-h-72 overflow-y-auto">
                        {templates.length === 0 ? (
                          <DropdownMenuItem disabled className="text-white/50">No templates</DropdownMenuItem>
                        ) : (
                          templates.map((t) => (
                            <DropdownMenuItem
                              key={t.id}
                              onClick={() => insertTemplate(t)}
                              className="text-white hover:bg-[#1a3a6a] cursor-pointer"
                            >
                              {t.name}
                            </DropdownMenuItem>
                          ))
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      title="Attach a photo or file (max 5MB)"
                      variant="outline"
                      className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-10 w-10 p-0 shrink-0"
                    >
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                    </Button>
                    <Textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={sending}
                      placeholder="Type your reply…"
                      rows={2}
                      className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sending || !draft.trim()}
                      className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-10 px-4"
                    >
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </Button>
                  </div>
                ) : conversation.status === "waiting" ? (
                  <p className="text-center text-sm text-white/40 py-2">
                    Claim this chat to start replying.
                  </p>
                ) : (
                  <p className="text-center text-sm text-white/40 py-2">
                    Owned by {conversation.assigned_admin_name} — viewing read-only.
                  </p>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminChat;
